import { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Input, MessageBarBody, MessageBarTitle, Select } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { AppState } from '../../../redux/reducers';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';
import MedicalServiceSelectorComponent from '../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent';

// guía
import { GuideDrawer, GuideDrawerForm, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

import * as ComponentFunctions from './PriceListItemForm.Functions';
import './PriceListItemForm.Styles.css';

/**
 * Alta y edición de un precio (guía, drawer medium): sección "Servicio y precio" con el
 * selector de servicio, moneda e importes en grilla de campos; errores debajo de cada campo,
 * banner si el servidor rechaza (precio duplicado u otro error). Misma lógica y payloads que antes.
 */
const PriceListItemFormComponentBase = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const PriceState = useSelector((appState: AppState) => appState.PriceDetails);

  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [formState, setFormState] = useState<ComponentFunctions.PriceFormState>(ComponentFunctions.initialPriceFormState);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (props.PriceDetails) {
      setFormState(ComponentFunctions.formStateFromPriceDetails(props.PriceDetails));
      setSubmittedForm(false);
    } else {
      ResetForm();
    }
  }, [props.PriceDetails, props.isOpen]);

  useEffect(() => {
    if (submittedForm && PriceState && (PriceState.successAddingNewOne || PriceState.successUpdatingOne)) {
      ResetForm();
      props.dismissPanel();
    }
  }, [PriceState?.successAddingNewOne, PriceState?.successUpdatingOne, submittedForm]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ────────────────────────────────────────────────────────────────

  const ResetForm = () => {
    setFormState(ComponentFunctions.initialPriceFormState);
    setSubmittedForm(false);
  };

  const patch = (partial: Partial<ComponentFunctions.PriceFormState>) => setFormState((s) => ({ ...s, ...partial }));

  const numberField = (key: keyof ComponentFunctions.PriceFormState, labelKey: string, required = false) => (
    <GuideField
      key={key}
      label={t(labelKey)}
      required={required}
      hint={required ? t('priceListItemForm.priceHint') : undefined}
      error={required && submittedForm && formState.price === undefined ? requiredMessage : undefined}
    >
      <Input
        type="number"
        min={0}
        step="0.01"
        value={formState[key] === undefined ? '' : String(formState[key])}
        onChange={(_event, data) =>
          patch({ [key]: data.value === '' ? (required ? undefined : 0) : Number(data.value) } as Partial<ComponentFunctions.PriceFormState>)
        }
      />
    </GuideField>
  );

  // ── Events ─────────────────────────────────────────────────────────────────

  const HandleCancelForm = () => {
    ResetForm();
    props.dismissPanel();
  };

  const HandleSaveForm = () => {
    setSubmittedForm(true);
    const validation = ComponentFunctions.validateForm(formState, t);
    if (!validation.success) return;
    if (props.PriceDetails) {
      props.SavePrice(ComponentFunctions.buildPricePayload(formState, props.PriceDetails, props.PriceListId));
    } else {
      props.AddNewPrice(ComponentFunctions.buildNewPricePayload(formState, props.PriceListId));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const isSaving = (PriceState?.isAddingNewOne || PriceState?.isUpdatingOne) ?? false;
  const saveFailed = !!(submittedForm && PriceState && (PriceState.failOnAddingNewOne || PriceState.failOnUpdatingOne) && PriceState.error);
  const isDuplicate = saveFailed && (PriceState?.error?.ErrorCode === 400 || PriceState?.error?.ErrorCode === 409);
  const requiredMessage = t('priceListItemForm.parameters.requiredField');
  const isEdit = !!props.PriceDetails;

  return (
    <GuideDrawer
      open={props.isOpen}
      size="medium"
      eyebrow={isEdit ? props.PriceDetails?.itemReferredName || undefined : undefined}
      title={isEdit ? t('priceListItemForm.modal.editTitle') : t('priceListItemForm.createTitle')}
      onDismiss={HandleCancelForm}
      preventDismiss={isSaving}
      closeLabel={t('priceListItemForm.modal.closeButton')}
      testId="price-list-item-drawer"
      secondaryAction={{ label: t('priceListItemForm.footer.cancelButton'), onClick: HandleCancelForm, disabled: isSaving }}
      primaryAction={{ label: t('priceListItemForm.footer.saveButton'), onClick: HandleSaveForm, loading: isSaving }}
    >
      {saveFailed && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="price-item-error">
          <MessageBarBody>
            <MessageBarTitle>{t('priceListItemForm.saveFailed')}</MessageBarTitle>
            {isDuplicate ? t('medicalSpecialties.priceListItemForm.failMessage.duplicatePrice') : PriceState?.error?.ErrorMessage}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      <GuideDrawerForm onSubmit={HandleSaveForm} testId="price-list-item-form">
        <GuideDrawerSection title={t('priceListItemForm.section')} description={t('priceListItemForm.serviceHint')}>
          {/* El selector trae su propia etiqueta; el error va debajo como en cualquier campo de la guía. */}
          <div className="ichtys-field" data-testid="price-item-service">
            <MedicalServiceSelectorComponent
              onSelectedServices={(services: AvailableMedicalService[] | undefined) => {
                if (services && services.length > 0) {
                  patch({ itemReferredId: services[0].id, itemReferredName: services[0].serviceNameReference });
                } else {
                  patch({ itemReferredId: undefined, itemReferredName: undefined });
                }
              }}
              MedicalServicesSelectedIds={formState.itemReferredId ? [formState.itemReferredId] : undefined}
              MultiselectEnable={false}
            />
            {submittedForm && formState.itemReferredId === undefined && (
              <span className="price-item-form__error" role="alert">{t('priceListItemForm.formValidation.selectServiceError')}</span>
            )}
          </div>

          <GuideFieldGrid>
            <GuideField
              label={t('priceListItemForm.parameters.currency')}
              required
              error={submittedForm && !formState.currency ? requiredMessage : undefined}
            >
              <Select value={formState.currency ?? ''} onChange={(_event, data) => patch({ currency: data.value || undefined })}>
                <option value="">{t('priceListItemForm.parameters.selectCurrency')}</option>
                {ComponentFunctions.currencyOptions.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.text}</option>
                ))}
              </Select>
            </GuideField>

            {props.showPrice && numberField('price', 'priceListItemForm.parameters.price', true)}
            {props.showRemotePrice && numberField('remotePrice', 'priceListItemForm.parameters.remotePrice')}
            {props.showPreScreeningPrice && numberField('preScreeningPrice', 'priceListItemForm.parameters.preScreeningPrice')}
            {props.showFailPrice && numberField('failPrice', 'priceListItemForm.parameters.failPrice')}
            {props.showRandomPrice && numberField('randomPrice', 'priceListItemForm.parameters.randomPrice')}
            {props.showAdditionalPaymentPrice && numberField('additionalPaymentPrice', 'priceListItemForm.parameters.additionalPaymentPrice')}
          </GuideFieldGrid>
        </GuideDrawerSection>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export const PriceListItemFormComponent = connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PriceListItemFormComponentBase as any) as any;

export default PriceListItemFormComponent;
